import { ObjectMapper } from 'json-object-mapper';
import { BaseEnum } from 'services/enums/baseEnum';
import { RecruitmentVacancyPurpose } from 'services/enums/RecruitmentVacancyPurpose.enum';
import { IModel } from '../IModel';
import { Model } from '../model';
import { JobModality } from './enum/JobModality.enum';
import { JobType } from './enum/JobType.enum';
import { RecruitmentStatus } from './enum/RecruitmentStatus.enum';
import { RecruitmentCustomFieldItemModel } from './recruitment-custom-field-item.model';

export class RecruitmentModel extends Model implements IModel<RecruitmentModel> {
  public code: number = 0;
  public title: string = '';
  public jobTitleFamilyId: string | null = null;
  public jobTitleFamilyName: string = '';
  public jobTitleGroupId: string = '';
  public jobTitleGroupName: string = '';
  public jobTitleId: string | null = null;
  public jobTitleName: string = '';
  public timeshiftId: string | null = null;
  public timeshiftName: string = '';
  public salaryScaleId: string = '';
  public salaryScaleName: string = '';
  public salaryString: string = '';
  public salary: string = '';
  public displaySalary: boolean = true;
  public noPublication: boolean = false;
  public type: number | string = '';
  public modality: number | string = '';
  public publicationStartDate: string | null = null;
  public publicationEndDate: string | null = null;
  public completionDeadline: string | null = null;
  public finalizationDate: string | null = null;
  public workforcePlanId: string | null = null;
  public companyId: string = '';
  public companyGroupId: string = '';
  public companyName: string = '';
  public branchId: string = '';
  public branchName: string = '';
  public costCenterId: string = '';
  public costCenterName: string = '';
  public recruiterId: string = '';
  public recruiterName: string = '';
  public managerId: string = '';
  public managerName: string = '';
  public description: string = '';
  public responsabilities: string = '';
  public technicalRequirements: string = '';
  public behavioralRequirements: string = '';
  public benefits: string = '';
  public competencyIds: string[] = [];
  public customFields: RecruitmentCustomFieldItemModel[] = [];
  public location: string = '';
  public companyCity: string = '';
  public companyState: string = '';
  public address: string = '';
  public addressMapLink: string = '';
  public amountPositions: number = 1;
  public purpose: number | string | null = null;
  public movementRequestLinks: Array<{ replacedEmployeeId?: string; replacedEmployeeName?: string }> = [];
  public status: string = RecruitmentStatus.DRAFT.value;
  public totalCandidates: number = 0;
  public daysOpen: number = 0;
  public pauseReasonId: string | null = null;
  public pauseReasonTitle: string = '';
  public pauseJustification: string = '';
  public pausedAt: string | Date | null = null;
  public statusBeforePause: number | string | null = null;

  /** When true, API copies description / responsabilities / requirements to the linked job title group. */
  public updateJobTitleGroupFromRecruitment: boolean = false;

  private getEnum<T extends { getById(id: number): any; getBy(key: string): any }>(
    enumType: T,
    value: number | string
  ): BaseEnum | null {
    if (value == null) return null;

    const item =
      typeof value === 'number'
        ? enumType.getById(value)
        : (enumType.getBy(value as any) as BaseEnum);
    return item;
  }

  public get typeLabel(): string | null {
    return this.getEnum(JobType, this.type)?.label ?? null;
  }

  public get typeId(): number | null {
    return this.getEnum(JobType, this.type)?.id ?? null;
  }

  public get modalityLabel(): string | null {
    return this.getEnum(JobModality, this.modality)?.label ?? null;
  }

  public get modalityId(): number | null {
    return this.getEnum(JobModality, this.modality)?.id ?? null;
  }

  public get formatLocation() {
    const parts = [this.branchName, this.companyName].filter(p => p && p.trim());

    return parts.length > 0 ? parts.join(' - ') : 'Não especificado';
  }

  get typeBadge(): string {
    const type = this.getEnum(JobType, this.type);
    return `<span class="badge badge-light-${type?.className} rounded-pill text-wrap">${type?.label}</span>`;
  }

  get modalityBadge(): string {
    const modality = this.getEnum(JobModality, this.modality);
    if (!modality) {
      return '';
    }
    return `<span class="badge badge-light-${modality.className} rounded-pill text-wrap">${modality.label}</span>`;
  }

  get statusBadge(): string {
    const status = this.statusInfo;

    return `<span class="badge badge-light-${status?.className} text-wrap text-left">${status?.label}</span>`;
  }

  get statusInfo(): RecruitmentStatus | undefined {
    const statusValue: number | string | null | undefined =
      this.status ?? (this as { Status?: number | string }).Status;

    if (statusValue == null || statusValue === '') {
      return undefined;
    }

    if (typeof statusValue === 'number') {
      return RecruitmentStatus.getById(statusValue);
    }

    return RecruitmentStatus.getBy(statusValue);
  }

  get purposeLabel(): string | null {
    if (this.purpose == null || this.purpose === '') {
      return null;
    }
    return RecruitmentVacancyPurpose.resolve(this.purpose)?.label ?? null;
  }

  get replacedEmployeeNames(): string[] {
    return (this.movementRequestLinks || [])
      .map((link) => link?.replacedEmployeeName)
      .filter((name): name is string => !!name && !!name.trim());
  }

  get isPaused(): boolean {
    return this.statusInfo?.id === RecruitmentStatus.STOPPED.id;
  }

  get canPause(): boolean {
    const statusId = this.statusInfo?.id;
    return (
      statusId === RecruitmentStatus.PUBLISHED.id || statusId === RecruitmentStatus.IN_PROCESS.id
    );
  }

  get canReopen(): boolean {
    const statusId = this.statusInfo?.id;
    return (
      statusId === RecruitmentStatus.INTERRUPTED.id || statusId === RecruitmentStatus.STOPPED.id
    );
  }

  static create(input: any): RecruitmentModel {
    const model = ObjectMapper.deserialize<RecruitmentModel>(RecruitmentModel, input);
    if (input) {
      model.code = input.code ?? model.code;
      model.purpose = input.purpose ?? model.purpose;
      model.movementRequestLinks = input.movementRequestLinks ?? model.movementRequestLinks ?? [];
      model.pauseReasonId = input.pauseReasonId ?? model.pauseReasonId;
      model.pauseReasonTitle = input.pauseReasonTitle ?? model.pauseReasonTitle;
      model.pauseJustification = input.pauseJustification ?? model.pauseJustification;
      model.pausedAt = input.pausedAt ?? model.pausedAt;
      model.statusBeforePause = input.statusBeforePause ?? model.statusBeforePause;
    }
    return model;
  }

  public toJSON(): string {
    const raw = ObjectMapper.serialize(this) as any;
    const serialized = typeof raw === 'string' ? JSON.parse(raw) : { ...raw };
    delete serialized.pauseReasonId;
    delete serialized.pauseReasonTitle;
    delete serialized.pauseJustification;
    delete serialized.pausedAt;
    delete serialized.statusBeforePause;
    delete serialized.code;
    return JSON.stringify(serialized);
  }
}
